import React, { useEffect, useMemo } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
  PaginationProvider,
} from 'react-bootstrap-table2-paginator';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  getHandlerTableChange,
  NoRecordsFoundMessage,
  PleaseWaitMessage,
  sortCaret,
  headerSortingClasses,
} from '../../../../../../_metronic/_helpers';
import { injectIntl } from 'react-intl';
import { Pagination } from '../../../../../../_metronic/_partials/controls';
import * as uiHelpers from '../StocksUIHelpers';
import * as columnFormatters from './column-formatters';
import { useStocksUIContext } from '../StocksUIContext';
import * as actions from '../../../_redux/stocks/stocksActions';

const StocksTable = (props) => {
  const { intl } = props;
  const orderDateLabel = intl.formatMessage({
    id: 'ORDER.LABEL.ORDERDATE',
  });
  const orderCodeLabel = intl.formatMessage({
    id: 'STOCK.LABEL.ORDERCODE',
  });

  const trackingIdLabel = intl.formatMessage({
    id: 'STOCK.LABEL.TRACKINGID',
  });
  const createdAtLabel = intl.formatMessage({
    id: 'STOCK.LABEL.CREATEDAT',
  });
  const stockLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.NAME',
  });

  const brandLabel = intl.formatMessage({
    id: 'PRODUCT.LABEL.BRAND',
  });
  const qtyLabel = intl.formatMessage({
    id: 'STOCK.LABEL.QTY',
  });
  const restLabel = intl.formatMessage({
    id: 'STOCK.LABEL.REST',
  });
  const adjustTypeLabel = intl.formatMessage({
    id: 'STOCK.LABEL.ADJUSTTYPE',
  });
  const costLabel = intl.formatMessage({
    id: 'STOCK.LABEL.COST',
  });
  const actionsLabel = intl.formatMessage({ id: 'STOCK.LABEL.ACTIONS' });

  // Stocks UI Context
  const StocksUIContext = useStocksUIContext();
  const StocksUIProps = useMemo(() => {
    return {
      queryParams: StocksUIContext.queryParams,
      setQueryParams: StocksUIContext.setQueryParams,
      openViewStockDialog: StocksUIContext.openViewStockDialog,
      openDeleteStockDialog: StocksUIContext.openDeleteStockDialog,
    };
  }, [StocksUIContext]);

  // Getting curret state of Stocks list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.stocks }),
    shallowEqual
  );
  const { totalCount, entities, listLoading } = currentState
    ? currentState
    : { totalCount: 0, entities: [], listLoading: false };

  // Stocks Redux state
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(actions.fetchStocks(StocksUIProps.queryParams));
  }, [StocksUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: 'icon',
      text: '',
      sort: false,
      style: {
        width: '65px',
      },
      formatter: columnFormatters.IconColumnFormatter,
    },
    {
      dataField: 'name',
      text: stockLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.NameColumnFormatter,
    },
    {
      dataField: 'brandName',
      text: brandLabel,
      sort: true,
      sortCaret: sortCaret,
      style: {
        width: '80px',
      },
      formatter: columnFormatters.CountColumnFormatter,
    },
    {
      dataField: 'cost',
      text: costLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.PriceColumnFormatter,
      style: {
        width: '90px',
      },
      classes: 'text-rigth',
    },
    {
      dataField: 'qty',
      text: qtyLabel,
      sort: true,
      sortCaret: sortCaret,
      style: {
        width: '70px',
      },
      formatter: columnFormatters.CountColumnFormatter,
    },
    {
      dataField: 'rest',
      text: restLabel,
      sort: true,
      sortCaret: sortCaret,
      style: {
        width: '70px',
      },
      formatter: columnFormatters.CountColumnFormatter,
    },

    {
      dataField: 'orderDate',
      text: orderDateLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      style: {
        width: '75px',
      },
      classes: 'text-center',
    },
    {
      dataField: 'orderCode',
      text: orderCodeLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      style: {
        width: '100px',
      },
      classes: 'text-center',
    },
    /*   {
      dataField: 'trackingId',
      text: trackingIdLabel,
      sort: true,
      sortCaret: sortCaret,
    }, */
    {
      dataField: 'createdAt',
      text: createdAtLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      style: {
        width: '75px',
      },
      classes: 'text-center',
    },
    {
      dataField: 'adjustType',
      text: adjustTypeLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.TypeColumnFormatter,
      style: {
        width: '80px',
      },
    },

    {
      dataField: 'action',
      text: actionsLabel,
      formatter: columnFormatters.ActionsColumnFormatter,
      formatExtraData: {
        openViewStockDialog: StocksUIProps.openViewStockDialog,
        openDeleteStockDialog: StocksUIProps.openDeleteStockDialog,
      },
      classes: 'text-right pr-0',
      headerClasses: 'text-right pr-3',
      style: {
        width: '96px',
      },
    },
  ];
  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: StocksUIProps.queryParams.pageSize,
    page: StocksUIProps.queryParams.pageNumber,
  };
  return (
    <>
      <PaginationProvider pagination={paginationFactory(paginationOptions)}>
        {({ paginationProps, paginationTableProps }) => {
          return (
            <Pagination
              isLoading={listLoading}
              paginationProps={paginationProps}
            >
              <BootstrapTable
                wrapperClasses='table-responsive'
                bstocked={false}
                classes='table table-head-custom table-vertical-center overflow-hidden'
                bootstrap4
                remote
                keyField='id'
                data={entities === null ? [] : entities}
                columns={columns}
                defaultSorted={uiHelpers.defaultSorted}
                onTableChange={getHandlerTableChange(
                  StocksUIProps.setQueryParams
                )}
                useSortBy
                {...paginationTableProps}
              >
                <PleaseWaitMessage entities={entities} />
                <NoRecordsFoundMessage entities={entities} />
              </BootstrapTable>
            </Pagination>
          );
        }}
      </PaginationProvider>
    </>
  );
};

export default injectIntl(StocksTable);
